'use client'

import { useEffect, useState } from 'react'

import { useFraudStore, useImportScript } from '@rewardops/ui'
import { Logger, type WindowType } from '@rewardops/utils'

declare let window: WindowType & {}

interface CyberSourceProps {
  apiUrl: string
  orgId: string
  merchantId: string
}

const CyberSource = ({ apiUrl, orgId, merchantId }: CyberSourceProps) => {
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false)
  const setFraudSessionId = useFraudStore((state) => state.setFraud)
  const [cybersourceSessionId, setCybersourceSessionId] = useState<
    string | null
  >(null)

  // Generate a session ID if not already created
  useEffect(() => {
    if (!cybersourceSessionId) {
      const sessionId = `${merchantId}${new Date().getTime()}`
      setCybersourceSessionId(sessionId)
    }
  }, [merchantId, cybersourceSessionId])

  const [isLoaded, error] = useImportScript(
    cybersourceSessionId
      ? `${apiUrl}?org_id=${orgId}&session_id=${cybersourceSessionId}`
      : '',
    {
      async: false,
      id: 'cybersource-script',
      dataSessionId: cybersourceSessionId as any,
    },
    () => {
      if (hasAttemptedLoad || !cybersourceSessionId) {
        return
      }

      setFraudSessionId((cybersourceSessionId ?? undefined) as any)

      const cybersourceTimeout = setTimeout(() => {
        const pureSessionId = cybersourceSessionId?.replace(merchantId, '')
        if (pureSessionId) {
          setFraudSessionId(pureSessionId)
        }
      }, 5000)

      const iframe = document.getElementById('dfIframe')
      if (iframe) {
        iframe.setAttribute('aria-label', 'Anti-fraud verification')
      }

      const scriptTag = document.getElementById('cybersource-script')
      if (!scriptTag) {
        const newScriptTag = document.createElement('script')
        newScriptTag.id = 'cybersource-script'
        newScriptTag.setAttribute('data-session-id', cybersourceSessionId)
        document.body.appendChild(newScriptTag)
      }

      return () => {
        clearTimeout(cybersourceTimeout)
      }
    },
  )

  useEffect(() => {
    if (!hasAttemptedLoad && (isLoaded || error)) {
      setHasAttemptedLoad(true)
    }

    if (error) {
      Logger.error('Error loading CyberSource script:', error)
    }
  }, [isLoaded, error, hasAttemptedLoad])

  return undefined
}

export { CyberSource }
